import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Grid3x3, Share2, Heart, X, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import * as Lucide from "lucide-react";

/**
 * Floating overlay controls for immersive pages (live / videos).
 * Provides: Home, Categories dropdown, Like, Share.
 */
export default function ImmersiveControls({
  liked = false,
  onLike,
  shareTitle = "Check this out on ShopRizz",
  className = "",
  extraRight = null,
}) {
  const [catsOpen, setCatsOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [categories, setCategories] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!categories.length) {
      api.get("/categories").then(({ data }) => setCategories(data)).catch(()=>{});
    }
  }, [categories.length]);

  // close dropdown on outside click
  useEffect(() => {
    if (!catsOpen) return;
    const onDoc = (e) => { if (!dropdownRef.current?.contains(e.target)) setCatsOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [catsOpen]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, url });
        return;
      }
    } catch { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(url);
      setToast("Link copied 🔗");
      setTimeout(() => setToast(null), 1800);
    } catch {
      setToast("Copy failed");
      setTimeout(() => setToast(null), 1800);
    }
  };

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Home */}
        <Link
          to="/"
          data-testid="immersive-home-btn"
          className="w-9 h-9 rounded-full bg-black/60 text-white hover:bg-[#D4FF00] hover:text-black flex items-center justify-center active:scale-95 transition shrink-0"
          aria-label="Home"
        >
          <Home size={16} strokeWidth={2.5}/>
        </Link>

        {/* Categories dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setCatsOpen(o => !o)}
            data-testid="immersive-cats-btn"
            className="w-9 h-9 rounded-full bg-black/60 text-white hover:bg-[#D4FF00] hover:text-black flex items-center justify-center active:scale-95 transition"
            aria-label="Categories"
          >
            <Grid3x3 size={16} strokeWidth={2.5}/>
          </button>
          {catsOpen && (
            <div className="absolute left-0 top-12 z-50 w-64 bg-[#261534] border border-[#3D2845] rounded-2xl p-2 shadow-2xl max-h-[60vh] overflow-y-auto">
              <div className="px-3 py-2 flex items-center justify-between">
                <span className="font-display font-bold uppercase text-xs tracking-widest text-[#A1A1AA]">Categories</span>
                <button onClick={()=>setCatsOpen(false)}><X size={14}/></button>
              </div>
              {categories.map((c) => {
                const Icon = Lucide[c.icon] || Lucide.Package;
                return (
                  <Link
                    key={c.category_id}
                    to={`/category/${c.category_id}`}
                    onClick={()=>setCatsOpen(false)}
                    data-testid={`immersive-cat-${c.category_id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#3D2845] text-sm font-semibold"
                  >
                    <Icon size={16} strokeWidth={2.5} className="text-[#D4FF00] shrink-0"/>
                    <span className="flex-1 truncate">{c.name}</span>
                    <ChevronRight size={14} className="text-[#A1A1AA]"/>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Like */}
        {onLike && (
          <button
            onClick={onLike}
            data-testid="immersive-like-btn"
            className={`w-9 h-9 rounded-full flex items-center justify-center active:scale-90 transition shrink-0 ${
              liked ? "bg-[#FF007F] text-white" : "bg-black/60 text-white hover:bg-[#FF007F]"
            }`}
            aria-label="Like"
          >
            <Heart size={16} strokeWidth={2.5} className={liked ? "fill-white" : ""}/>
          </button>
        )}

        {/* Share */}
        <button
          onClick={handleShare}
          data-testid="immersive-share-btn"
          className="w-9 h-9 rounded-full bg-black/60 text-white hover:bg-[#00F0FF] hover:text-black flex items-center justify-center active:scale-90 transition shrink-0"
          aria-label="Share"
        >
          <Share2 size={16} strokeWidth={2.5}/>
        </button>

        {extraRight}
      </div>

      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-[#D4FF00] text-black font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-full z-50 fade-up shadow-lg" data-testid="share-toast">
          {toast}
        </div>
      )}
    </>
  );
}

/** LocalStorage-based favorites (used when no backend fav endpoint exists). */
export function useLocalLike(key) {
  const [liked, setLiked] = React.useState(() => {
    try { return !!JSON.parse(localStorage.getItem("o2s_likes") || "{}")[key]; }
    catch { return false; }
  });
  const toggle = React.useCallback(() => {
    setLiked((prev) => {
      const next = !prev;
      try {
        const all = JSON.parse(localStorage.getItem("o2s_likes") || "{}");
        if (next) all[key] = true; else delete all[key];
        localStorage.setItem("o2s_likes", JSON.stringify(all));
      } catch {}
      return next;
    });
  }, [key]);
  return [liked, toggle];
}
